import {html, LitElement} from '@isceco/widget-library2/external/lit'
import {getFomanticStylesheet} from '@isceco/widget-library2/assets/fomantic-icons-styles'
import EformSubmitStatusCss from './EformSubmitStatusCss'
import '@isceco/widget-library2/basic-elements/Card/Card'
import '@isceco/widget-library2/basic-elements/Spinner/Spinner'

export default class EformSubmitStatus extends LitElement {

  static get EVENT() {
    return {
      LOADING: 'eform-submit-status-loading',
      SUCCESS: 'eform-submit-status-succeed',
      ERROR: 'eform-submit-status-failed',
      CLOSE: 'eform-submit-status-closed'
    }
  }

  static get styles() {
    return [EformSubmitStatusCss]
  }

  constructor() {
    super()
    this.text = ''
    this.details = ''
    this.icon = ''
    this.cardColor = ''
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(EformSubmitStatus.EVENT.LOADING, this.handleLoading)
    document.addEventListener(EformSubmitStatus.EVENT.SUCCESS, this.handleSuccess)
    document.addEventListener(EformSubmitStatus.EVENT.ERROR, this.handleError)
    document.addEventListener(EformSubmitStatus.EVENT.CLOSE, this.handleClose)
    getFomanticStylesheet()
      .then(stylesheet => {
        this.shadowRoot.adoptedStyleSheets = [...this.shadowRoot.adoptedStyleSheets, stylesheet]
      })
      .catch(error => {
        throw error
      })
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(EformSubmitStatus.EVENT.LOADING, this.handleLoading)
    document.removeEventListener(EformSubmitStatus.EVENT.SUCCESS, this.handleSuccess)
    document.removeEventListener(EformSubmitStatus.EVENT.ERROR, this.handleError)
    document.removeEventListener(EformSubmitStatus.EVENT.CLOSE, this.handleClose)
  }

  render() {
    return html`
      <isceco-card id="submit-status" class="hidden" style="background-color: var(${this.cardColor})">
        <div class="status">
          <span>${this.icon}</span>
          <span id="message">${this.text}</span>
        </div>
        <div class="details">
          ${this.details}
        </div>
        <div id="quittung-button" class="hidden noprint">
          <isceco-link @click="${() => window.print()}"
                       text="${translateText(this._translations(), 'print')}"
                       icon="print"
          ></isceco-link>
        </div>
      </isceco-card>
    `
  }

  handleLoading = event => {
    event.target.classList.toggle('hidden', true) // hide form
    const {text} = event.detail

    this.text = !isEmpty(text) ? text : translateText(this._translations(), 'sending')
    this.icon = html`<isceco-spinner></isceco-spinner>`
    this.cardColor = '--isceco-color-white-500'

    this._toggleVisibility(false)
  }

  handleSuccess = event => {
    const {formUuid, timestamp, formName, email} = event.detail

    this.text = html`
      ${translateText(this._translations(), 'success')}
      <isceco-link text="${translateText(this._translations(), 'reload')}"
                   class="noprint"
                   @click="${() => window.location.reload()}"
      ></isceco-link>
    `
    this.details = html`
      <p><b>${translateText(this._translations(), 'quittung_title')}</b></p>
      <table>
        <tr>
          <td>${translateText(this._translations(), 'quittung_formName')}</td>
          <td>${formName}</td>
        </tr>
        <tr>
          <td>${translateText(this._translations(), 'quittung_formUuid')}</td>
          <td>${formUuid}</td>
        </tr>
        <tr>
          <td>${translateText(this._translations(), 'quittung_timestamp')}</td>
          <td>${timestamp}</td>
        </tr>
        <tr>
          <td>${translateText(this._translations(), 'quittung_email')}</td>
          <td>${email}</td>
        </tr>
      </table>
    `
    this.icon = html`<i class="icon check green"></i>`
    this.cardColor = '--isceco-color-green-100'

    this.shadowRoot.getElementById('quittung-button').classList.toggle('hidden', false)
    this._toggleVisibility(false)
  }

  handleError = event => {
    event.target.classList.toggle('hidden', false) // show form
    const {title, detail, reasons} = event.detail

    this.text = title ?? translateText(this._translations(), 'error')
    this.details = html`
      <p>${detail}</p>
      <div>
        <ul>
          ${reasons?.map(r => html`<li>${r.field}: ${r.text}</li>`)}
        </ul>
      </div>
    `
    this.icon = html`<i class="icon exclamation red"></i>`
    this.cardColor = '--isceco-color-red-100'

    this._toggleVisibility(false)
  }

  handleClose = () => {
    this._toggleVisibility(true)
  }

  _toggleVisibility = hidden => {
    this.shadowRoot.getElementById('submit-status').classList.toggle('hidden', hidden)
    this.requestUpdate()
  }

  _translations = () => ({
    de: {
      sending: 'Sendung läuft, bitte warten Sie, bevor Sie die Seite verlassen.',
      success: 'Formular versendet.',
      error: 'Fehler bei Formular absendung.',
      print: 'Quittung drucken',
      reload: 'Neues Formular ausfüllen',
      quittung_title: 'Versanddetails',
      quittung_formName: 'Formular:',
      quittung_formUuid: 'Formular ID:',
      quittung_timestamp: 'Datum und Uhrzeit:',
      quittung_email: 'Email versender:'
    },
    fr: {
      sending: 'Envoi en cours, veuillez svp. patienter avant de quitter la page.',
      success: 'Formulaire envoyé.',
      error: 'Erreur lors de l\'envoi du formulaire.',
      print: 'Imprimer la confirmation',
      reload: 'Saisir un nouveau formulaire',
      quittung_title: 'Détails de l\'envoi',
      quittung_formName: 'Formulaire:',
      quittung_formUuid: 'Formulaire ID:',
      quittung_timestamp: 'Date et heure:',
      quittung_email: 'Email de l\'expéditeur:'
    },
    it: {
      sending: 'Programma in corso, attendere prima di lasciare la pagina.',
      success: 'Modulo inviato.',
      error: 'Errore durante l\'invio del modulo.',
      print: 'Stampa ricevuta',
      reload: 'Compilare il nuovo modulo',
      quittung_title: 'Dettagli',
      quittung_formName: 'Modulo:',
      quittung_formUuid: 'Modulo ID:',
      quittung_timestamp: 'Data e ora:',
      quittung_email: 'Invio e-mail:'
    },
    en: {
      sending: 'Sending in progress, please wait before leaving the page.',
      success: 'Form sent.',
      error: 'Error while sending form.',
      print: 'Print receipt',
      reload: 'Fill new form',
      quittung_title: 'Details',
      quittung_formName: 'Form:',
      quittung_formUuid: 'Form ID:',
      quittung_timestamp: 'Timestamp:',
      quittung_email: 'Email sender:'
    }
  })
}

customElements.define('eform-submit-status', EformSubmitStatus)
